import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import MainStructure from "../components/containers/MainStructure";
import { webReports } from "../api/webReports.service";
import HorseComparison from "../components/reports/HorseComparison";
import ShareButton from "../components/reports/ShareButton";
import {
  formatScore,
  horseName,
  metricDefinitions,
  modelName,
  rankedReports,
  score,
} from "../components/reports/reportMetrics";
import "../assets/styles/pages/webReports.scss";
import useWebReportMeta from "../hooks/useWebReportMeta";
import ReportAccessGate, {
  useReportAccess,
} from "../components/reports/ReportAccessGate";

const PAGE_SIZE = 20;
const TOP_HORSE_COUNTS = [5, 10, 20];
export default function ReportCollection() {
  const { token, kind, id } = useParams();
  return (
    <ReportAccessGate key={token || `${kind}:${id}`} collection>
      <ReportCollectionContent />
    </ReportAccessGate>
  );
}

function ReportCollectionContent() {
  const requireAuthentication = useReportAccess();
  const { token, kind, id } = useParams();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  useWebReportMeta(data?.name || "Horse comparison");
  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setError("");
    (id
      ? webReports.ownedCollection(kind, id, controller.signal)
      : webReports.collection(token, controller.signal))
      .then(setData)
      .catch((err) => {
        if (err.authenticationRequired) return requireAuthentication();
        if (err.name !== "AbortError") setError(err.message);
      });
    return () => controller.abort();
  }, [token, kind, id, reload, requireAuthentication]);
  const reports = useMemo(() => data?.results || [], [data]);
  const availableModels = [
    ...new Set(reports.map((report) => Number(report.ml_model))),
  ].sort();
  const requestedModel = Number(params.get("model"));
  const model = availableModels.includes(requestedModel)
    ? requestedModel
    : availableModels[0] || 1;
  const metric = [
    "score",
    ...metricDefinitions(model).map(({ key }) => key),
  ].includes(params.get("sort"))
    ? params.get("sort")
    : "score";
  const requestedTopCount = Number(params.get("top_count"));
  const topCount = TOP_HORSE_COUNTS.includes(requestedTopCount)
    ? requestedTopCount
    : 5;
  const search = params.get("q") || "";
  const sire = params.get("sire") || "";
  const dam = params.get("dam") || "";
  const tier = params.get("tier") || "";
  const band = params.get("band");
  const topOnly = params.get("top") === "1";
  const reportReturnState = {
    returnTo: `${location.pathname}${location.search}`,
    returnLabel: data?.kind === "premium" ? "Multi report" : "Catalogue report",
  };
  const ranked = useMemo(
    () => rankedReports(reports, model, metric),
    [reports, model, metric],
  );
  const scoreCeiling = Math.max(
    10,
    Math.ceil(
      ranked.reduce(
        (highest, report) => Math.max(highest, score(report) ?? 0),
        0,
      ),
    ),
  );
  const filtered = ranked.filter(
    (report) =>
      (!search ||
        `${horseName(report)} ${report.lot || ""} ${report.id}`
          .toLowerCase()
          .includes(search.toLowerCase())) &&
      (!sire || report.sire?.name === sire) &&
      (!dam || report.dam?.name === dam) &&
      (!tier ||
        (report.top_value != null &&
          Number(report.top_value) <= Number(tier))) &&
      (!topOnly || report.is_top_pick) &&
      (band == null ||
        (score(report) != null &&
          Math.min(scoreCeiling - 1, Math.floor(score(report))) ===
            Number(band))),
  );
  const selectedIds = (params.get("compare") || "")
    .split(",")
    .map(Number)
    .filter(Boolean);
  const selected = selectedIds
    .map((id) => ranked.find((report) => report.id === id))
    .filter(Boolean)
    .slice(0, 4);
  const requestedPage = Math.max(1, Number(params.get("page")) || 1);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const page = Math.min(requestedPage, pageCount);
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  function update(key, value) {
    const next = new URLSearchParams(params);
    value ? next.set(key, String(value)) : next.delete(key);
    if (key !== "page" && key !== "compare") next.delete("page");
    if (key === "model")
      ["compare", "sire", "dam", "sort", "band"].forEach((item) =>
        next.delete(item),
      );
    setParams(next, { replace: true });
  }
  function toggle(report) {
    const ids = selected.map((item) => item.id);
    update(
      "compare",
      ids.includes(report.id)
        ? ids.filter((id) => id !== report.id).join(",")
        : [...ids, report.id].slice(0, 4).join(","),
    );
  }
  const summary = data?.summary?.models.find(
    (item) => Number(item.ml_model) === model,
  );
  const bins = Array.from({ length: scoreCeiling }, (_, index) => ({
    index,
    count: ranked.filter(
      (report) =>
        score(report) != null &&
        Math.min(scoreCeiling - 1, Math.floor(score(report))) === index,
    ).length,
  }));
  const maxBin = Math.max(1, ...bins.map((bin) => bin.count));
  const uniqueNames = (role) =>
    [
      ...new Set(ranked.map((report) => report[role]?.name).filter(Boolean)),
    ].sort();
  const top = ranked
    .filter(
      (report) => (metric === "score" ? score(report) : report[metric]) != null,
    )
    .slice(0, topCount);
  const incomplete = data
    ? data.count -
      reports.filter((report) => report.status === "completed").length
    : 0;
  return (
    <MainStructure>
      <main className="web-report report-collection">
        <Link
          className="report-back"
          to={data?.kind === "premium" ? "/premium" : "/catalogues"}
        >
          ← {data?.kind === "premium" ? "Multi reports" : "Catalogue library"}
        </Link>
        {error ? (
          <section className="report-panel report-state" role="alert">
            <h1>Collection unavailable</h1>
            <p>{error}</p>
            <button onClick={() => setReload(reload + 1)}>Try again</button>
          </section>
        ) : !data ? (
          <div className="report-state" role="status">
            Loading all horses and comparison insights…
          </div>
        ) : (
          <>
            <header className="report-hero">
              <div>
                <p className="report-eyebrow">
                  {data.kind === "premium" ? "Multi report" : "Catalogue"}{" "}
                  intelligence
                </p>
                <h1>{data.name}</h1>
                <p>A clear view of the field. A closer look at every horse.</p>
              </div>
              <div className="report-hero__actions">
                {data.kind === "catalogue" && data.catalogue_id ? (
                  <Link className="report-buy-lots" to={`/catalogues?buy=${data.catalogue_id}`}>
                    <i className="fa fa-plus" aria-hidden="true" /> Buy more lots
                  </Link>
                ) : null}
                <ShareButton path={`${data.web_url || location.pathname}${params.toString() ? `?${params}` : ""}`} />
              </div>
            </header>
            <div
              className="report-model-switch"
              role="group"
              aria-label="Analysis model"
            >
              {availableModels.map((value) => (
                <button
                  key={value}
                  aria-pressed={model === value}
                  onClick={() => update("model", value)}
                >
                  {modelName(value)} models
                </button>
              ))}
            </div>
            {availableModels.length > 1 && (
              <p className="report-note">
                Compare horses within the same model. Global and AUS/NZ scores
                use different calibrations.
              </p>
            )}
            <div className="report-stats">
              {[
                ["Horses in collection", data.count],
                [`${modelName(model)} results ready`, summary?.count || 0],
                ["Average pedigree score", formatScore(summary?.average_score)],
                ["Best pedigree score", formatScore(summary?.best_score)],
                ["Top picks", summary?.top_picks || 0],
              ].map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
            {incomplete > 0 && (
              <div className="report-notice" role="status">
                {incomplete} analysis{incomplete === 1 ? "" : "es"} awaiting
                results or needing attention. Rankings and charts include
                completed results only.{" "}
                <button onClick={() => setReload(reload + 1)}>
                  Refresh results
                </button>
              </div>
            )}
            <div className="report-charts">
              <section className="report-panel">
                <div className="report-section-heading">
                  <div>
                    <p className="report-eyebrow">The strongest pedigrees</p>
                    <h2>Top of the field</h2>
                  </div>
                  <div className="report-ranking-controls">
                    <label>
                      Rank by
                      <select
                        value={metric}
                        onChange={(event) => update("sort", event.target.value)}
                      >
                        <option value="score">Pedigree score</option>
                        {metricDefinitions(model).map((item) => (
                          <option value={item.key} key={item.key}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Show
                      <select
                        aria-label="Number of top horses"
                        value={topCount}
                        onChange={(event) =>
                          update(
                            "top_count",
                            event.target.value === "5" ? "" : event.target.value,
                          )
                        }
                      >
                        {TOP_HORSE_COUNTS.map((count) => (
                          <option value={count} key={count}>
                            Top {count}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
                {top.length ? (
                  <ol className="report-ranking">
                    {top.map((report, index) => (
                      <li key={report.id}>
                        <span>{index + 1}</span>
                        <div>
                          <Link to={report.web_url} state={reportReturnState}>
                            {horseName(report)}
                          </Link>
                          <small>
                            {report.lot
                              ? `Lot ${report.lot}`
                              : `Report ${report.id}`}
                          </small>
                          <div className="report-ranking__bar">
                            <span
                              style={{
                                width: `${(metric === "score" ? score(report) / scoreCeiling : report[metric] / 6) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                        <strong>
                          {metric === "score"
                            ? formatScore(score(report))
                            : `${report[metric]} / 6`}
                        </strong>
                        <button
                          onClick={() => toggle(report)}
                          disabled={
                            !selected.some((item) => item.id === report.id) &&
                            selected.length >= 4
                          }
                          aria-label={`Compare ${horseName(report)}`}
                          aria-pressed={selected.some(
                            (item) => item.id === report.id,
                          )}
                        >
                          {selected.some((item) => item.id === report.id)
                            ? "Added"
                            : "+ Compare"}
                        </button>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p>No scored results yet.</p>
                )}
              </section>
              <section className="report-panel">
                <p className="report-eyebrow">Understand the whole field</p>
                <h2>Score distribution</h2>
                <p className="report-note">
                  Select a range to filter the horses below.
                </p>
                <div className="report-histogram">
                  {bins.map((bin) => (
                    <button
                      key={bin.index}
                      className={band === String(bin.index) ? "is-active" : ""}
                      aria-label={`Score ${bin.index} to ${bin.index + 1}: ${bin.count} horses`}
                      aria-pressed={band === String(bin.index)}
                      onClick={() =>
                        update(
                          "band",
                          band === String(bin.index)
                            ? ""
                            : bin.index.toString(),
                        )
                      }
                    >
                      <strong>{bin.count}</strong>
                      <span
                        className="report-histogram__column"
                        style={{ height: `${(bin.count / maxBin) * 130}px` }}
                      />
                      <small>
                        {bin.index}–{bin.index + 1}
                      </small>
                    </button>
                  ))}
                </div>
                <p className="report-note">
                  {ranked.filter((report) => score(report) == null).length}{" "}
                  completed reports without a recorded score.
                </p>
              </section>
            </div>
            <HorseComparison
              reports={selected}
              onRemove={(id) =>
                update(
                  "compare",
                  selected
                    .filter((report) => report.id !== id)
                    .map((report) => report.id)
                    .join(","),
                )
              }
              onClear={() => update("compare", "")}
              reportReturnState={reportReturnState}
            />
            <section className="report-panel" aria-labelledby="horses-title">
              <div className="report-section-heading">
                <div>
                  <p className="report-eyebrow">Explore and shortlist</p>
                  <h2 id="horses-title">
                    All horses <small>{filtered.length} results</small>
                  </h2>
                </div>
                {selected.length > 0 && (
                  <button
                    type="button"
                    className="report-primary-button"
                    onClick={() =>
                      document
                        .getElementById("compare")
                        ?.scrollIntoView({ block: "start" })
                    }
                  >
                    Compare selected ({selected.length}) ↑
                  </button>
                )}
              </div>
              <div className="report-toolbar">
                <label className="report-search">
                  Search horses
                  <input
                    type="search"
                    placeholder="Sire, dam, lot or report number"
                    value={search}
                    onChange={(event) => update("q", event.target.value)}
                  />
                </label>
                <label>
                  Sire
                  <select
                    value={sire}
                    onChange={(event) => update("sire", event.target.value)}
                  >
                    <option value="">All sires</option>
                    {uniqueNames("sire").map((name) => (
                      <option key={name}>{name}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Dam
                  <select
                    value={dam}
                    onChange={(event) => update("dam", event.target.value)}
                  >
                    <option value="">All dams</option>
                    {uniqueNames("dam").map((name) => (
                      <option key={name}>{name}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Population tier
                  <select
                    value={tier}
                    onChange={(event) => update("tier", event.target.value)}
                  >
                    <option value="">All tiers</option>
                    {[1, 5, 15, 50, 70, 90].map((value) => (
                      <option key={value} value={value}>
                        Top {value}% or better
                      </option>
                    ))}
                  </select>
                </label>
                <label className="report-check">
                  <input
                    type="checkbox"
                    checked={topOnly}
                    onChange={(event) =>
                      update("top", event.target.checked ? "1" : "")
                    }
                  />{" "}
                  Top picks only
                </label>
              </div>
              {(search || sire || dam || tier || topOnly || band != null) && (
                <button
                  className="report-clear"
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    ["q", "sire", "dam", "tier", "top", "band", "page"].forEach(
                      (key) => next.delete(key),
                    );
                    setParams(next, { replace: true });
                  }}
                >
                  Clear filters
                  {band != null
                    ? ` · Score range ${band}–${Number(band) + 1}`
                    : ""}
                </button>
              )}
              <p className="report-note">
                Select up to four horses for a side-by-side comparison. Your
                selection stays available as you search and filter; the shared
                link includes it.
              </p>
              <p className="report-note report-horse-table__method-note">
                Model ratings use a 0–6 scale. Crosses index scores 16 pedigree
                cells: 10–19.9% earns 1 point and 20% or more earns 2 points
                (maximum 32).
              </p>
              {filtered.length ? (
                <>
                  <div className="report-table-scroll">
                    <table className="report-horse-table">
                      <thead>
                        <tr>
                          <th scope="col">Compare</th>
                          <th scope="col">Lot / Report</th>
                          <th scope="col">Sire × Dam</th>
                          <th scope="col">Score</th>
                          <th scope="col" title="16 pedigree cross cells, maximum 32 points">Crosses index</th>
                          <th scope="col">Population tier</th>
                          {metricDefinitions(model).map(({ key, label }) => (
                            <th scope="col" key={key}>
                              {label}
                            </th>
                          ))}
                          <th scope="col">Report</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visible.map((report) => (
                          <tr
                            key={report.id}
                            className={
                              selected.some((item) => item.id === report.id)
                                ? "is-selected"
                                : ""
                            }
                          >
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`Select ${horseName(report)} for comparison`}
                                checked={selected.some(
                                  (item) => item.id === report.id,
                                )}
                                disabled={
                                  selected.length >= 4 &&
                                  !selected.some(
                                    (item) => item.id === report.id,
                                  )
                                }
                                onChange={() => toggle(report)}
                              />
                            </td>
                            <td>{report.lot || `#${report.id}`}</td>
                            <td>
                              <strong>
                                {report.sire?.name || "Unknown sire"}
                              </strong>
                              {report.sire?.country ? (
                                <small className="report-horse-table__country">
                                  {report.sire.country}
                                </small>
                              ) : null}
                              <span className="report-horse-table__dam">
                                × <strong>{report.dam?.name || "Unknown dam"}</strong>
                                {report.dam?.country ? (
                                  <small className="report-horse-table__country">
                                    {report.dam.country}
                                  </small>
                                ) : null}
                              </span>
                              {report.is_top_pick && (
                                <small className="report-chip">Top pick</small>
                              )}
                            </td>
                            <td>
                              <strong>{formatScore(score(report))}</strong>
                            </td>
                            <td>
                              {report.crosses_index == null
                                ? "—"
                                : `${report.crosses_index} / 32`}
                            </td>
                            <td>
                              {report.top_value != null
                                ? `Top ${report.top_value}%`
                                : "—"}
                            </td>
                            {metricDefinitions(model).map(({ key }) => (
                              <td key={key}>
                                {report[key] != null
                                  ? (
                                    <div className="report-horse-table__metric">
                                      <span>{report[key]} / 6</span>
                                      <span
                                        className="report-horse-table__metric-track"
                                        aria-hidden="true"
                                      >
                                        <span
                                          className={report[key] >= 6 ? "is-top" : ""}
                                          style={{
                                            width: `${Math.max(0, Math.min(6, Number(report[key])) / 6) * 100}%`,
                                          }}
                                        />
                                      </span>
                                    </div>
                                  )
                                  : "—"}
                              </td>
                            ))}
                            <td>
                              <Link
                                to={report.web_url}
                                state={reportReturnState}
                                aria-label={`View ${horseName(report)} report`}
                              >
                                View report →
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="report-pagination">
                    <span>
                      {(page - 1) * PAGE_SIZE + 1}–
                      {Math.min(page * PAGE_SIZE, filtered.length)} of{" "}
                      {filtered.length}
                    </span>
                    <button
                      disabled={page <= 1}
                      onClick={() => update("page", page - 1)}
                    >
                      Previous
                    </button>
                    <span>
                      Page {page} of {pageCount}
                    </span>
                    <button
                      disabled={page >= pageCount}
                      onClick={() => update("page", page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </>
              ) : (
                <div className="report-state">
                  <h3>
                    {ranked.length
                      ? "No horses match your filters"
                      : "No completed analyses yet"}
                  </h3>
                  <p>
                    {ranked.length
                      ? "Try another name or clear your filters to see the full field."
                      : "Refresh the results when the analysis has finished."}
                  </p>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </MainStructure>
  );
}
